// src/pages/admin/AdminCareers.jsx
import React, { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import {
  getJobs, addJob, updateJob, deleteJob,
  getCareerCulture, updateCareerCulture,
} from "../../services/firestoreService";
import Modal from "../../components/common/Modal";
import Spinner from "../../components/common/Spinner";
import ImageUploader from "../../components/common/ImageUploader";
import toast from "react-hot-toast";
import { FiPlus, FiEdit2, FiTrash2, FiBriefcase, FiImage } from "react-icons/fi";

export default function AdminCareers() {
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);

  // ── Culture section (the "Our Culture" split block on the Careers page) ──
  const [culture, setCulture] = useState({});
  const [cultureModalOpen, setCultureModalOpen] = useState(false);
  const [cultureImage, setCultureImage] = useState("");
  const [cultureSaving, setCultureSaving] = useState(false);
  const cultureForm = useForm();

  const { register, handleSubmit, reset, formState: { errors } } = useForm();

  const load = async () => {
    const [j, c] = await Promise.all([getJobs(), getCareerCulture()]);
    setJobs(j);
    setCulture(c || {});
    setLoading(false);
  };
  useEffect(() => { load(); }, []);

  // ── Career listing CRUD ──
  const openAdd = () => {
    setEditing(null);
    reset({
      title: "", department: "", location: "", type: "Full-time",
      postedAt: new Date().toISOString().slice(0, 10),
      description: "", requirements: "",
    });
    setModalOpen(true);
  };

  const openEdit = (job) => {
    setEditing(job);
    reset({
      title: job.title || "",
      department: job.department || "",
      location: job.location || "",
      type: job.type || "Full-time",
      postedAt: job.postedAt || new Date().toISOString().slice(0, 10),
      description: job.description || "",
      requirements: job.requirements || "",
    });
    setModalOpen(true);
  };

  const onSubmit = async (data) => {
    setSaving(true);
    try {
      if (editing) {
        await updateJob(editing.id, data);
        toast.success("Career updated");
      } else {
        await addJob(data);
        toast.success("Career added");
      }
      setModalOpen(false);
      load();
    } catch {
      toast.error("Save failed");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id, title) => {
    if (!window.confirm(`Delete career listing "${title}"?`)) return;
    await deleteJob(id);
    toast.success("Career deleted");
    setJobs((prev) => prev.filter((j) => j.id !== id));
  };

  // ── Culture section modal ──
  const openCultureModal = () => {
    setCultureImage(culture.image || "");
    cultureForm.reset({
      badge: culture.badge || "Our Culture",
      heading: culture.heading || "A Place Where Trust Meets Happiness",
      paragraph: culture.paragraph || "",
    });
    setCultureModalOpen(true);
  };

  const onCultureSubmit = async (data) => {
    setCultureSaving(true);
    try {
      const payload = { ...data, image: cultureImage };
      await updateCareerCulture(payload);
      setCulture(payload);
      toast.success("Culture section updated");
      setCultureModalOpen(false);
    } catch {
      toast.error("Save failed");
    } finally {
      setCultureSaving(false);
    }
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-bold text-gray-900">Careers</h1>
        <button onClick={openAdd} className="btn-primary flex text-sm sm:text-md items-center gap-2">
          <FiPlus className="w-4 h-4" /> Add Career Listing
        </button>
      </div>

      {/* ── Culture section editor ── */}
      <div className="card p-5 mb-6 flex items-center justify-between gap-4 flex-wrap">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg bg-primary-100 flex items-center justify-center text-primary-600 flex-shrink-0">
            <FiImage className="w-4 h-4" />
          </div>
          <div>
            <p className="font-semibold text-gray-900">Careers Page — Culture Section</p>
            <p className="text-sm text-gray-500 truncate max-w-md">
              {culture.heading || "A Place Where Trust Meets Happiness"}
            </p>
          </div>
        </div>
        <button onClick={openCultureModal} className="btn-secondary flex items-center gap-2 text-sm">
          <FiEdit2 className="w-4 h-4" /> Edit Section
        </button>
      </div>

      {loading ? <Spinner center /> : (
        <div className="card overflow-hidden">
          <div className="overflow-x-auto">
          <table className="w-full text-sm min-w-[640px]">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                {["Title", "Department", "Location", "Type", "Posted", "Actions"].map((h) => (
                  <th key={h} className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wide whitespace-nowrap">
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {jobs.length === 0 ? (
                <tr>
                  <td colSpan={6} className="text-center py-12 text-gray-500">
                    No career listings yet. Add your first one!
                  </td>
                </tr>
              ) : (
                jobs.map((job) => (
                  <tr key={job.id} className="hover:bg-gray-50">
                    <td className="px-4 py-3">
                      <span className="font-medium text-gray-900 flex items-center gap-2">
                        <FiBriefcase className="w-3.5 h-3.5 text-primary-500 flex-shrink-0" />
                        {job.title}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-gray-700">{job.department}</td>
                    <td className="px-4 py-3 text-gray-700">{job.location}</td>
                    <td className="px-4 py-3 text-gray-700">{job.type}</td>
                    <td className="px-4 py-3 text-gray-700">{job.postedAt}</td>
                    <td className="px-4 py-3">
                      <div className="flex gap-2">
                        <button onClick={() => openEdit(job)} className="p-1.5 rounded-lg hover:bg-primary-50 text-primary-600 transition-colors">
                          <FiEdit2 className="w-4 h-4" />
                        </button>
                        <button onClick={() => handleDelete(job.id, job.title)} className="p-1.5 rounded-lg hover:bg-red-50 text-red-500 transition-colors">
                          <FiTrash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
          </div>
        </div>
      )}

      {/* ── Add/Edit Career modal (no image field) ── */}
      <Modal isOpen={modalOpen} onClose={() => setModalOpen(false)} title={editing ? "Edit Career Listing" : "Add Career Listing"} size="lg">
        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
          <div>
            <label className="label">Career title *</label>
            <input {...register("title", { required: "Title is required" })} className={`input ${errors.title ? "border-red-400" : ""}`} placeholder="e.g. Field Sales Executive" />
            {errors.title && <p className="text-xs text-red-500 mt-1">{errors.title.message}</p>}
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="label">Department *</label>
              <input {...register("department", { required: "Department is required" })} className={`input ${errors.department ? "border-red-400" : ""}`} placeholder="e.g. Sales" />
              {errors.department && <p className="text-xs text-red-500 mt-1">{errors.department.message}</p>}
            </div>
            <div>
              <label className="label">Location *</label>
              <input {...register("location", { required: "Location is required" })} className={`input ${errors.location ? "border-red-400" : ""}`} placeholder="e.g. Ganjam, Odisha" />
              {errors.location && <p className="text-xs text-red-500 mt-1">{errors.location.message}</p>}
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="label">Career type</label>
              <select {...register("type")} className="input">
                <option>Full-time</option>
                <option>Part-time</option>
                <option>Contract</option>
                <option>Internship</option>
              </select>
            </div>
            <div>
              <label className="label">Posted date</label>
              <input type="date" {...register("postedAt")} className="input" />
            </div>
          </div>

          <div>
            <label className="label">Description *</label>
            <textarea {...register("description", { required: "Description is required" })} rows={4} className={`input resize-none ${errors.description ? "border-red-400" : ""}`} placeholder="Role summary shown to applicants" />
            {errors.description && <p className="text-xs text-red-500 mt-1">{errors.description.message}</p>}
          </div>

          <div>
            <label className="label">Requirements</label>
            <textarea {...register("requirements")} rows={3} className="input resize-none" placeholder="Skills, experience, qualifications" />
          </div>

          <div className="flex gap-3 pt-2">
            <button type="submit" disabled={saving} className="btn-primary flex-1 flex items-center justify-center gap-2">
              {saving ? <><Spinner size="sm" /> Saving...</> : editing ? "Update Career" : "Add Career"}
            </button>
            <button type="button" onClick={() => setModalOpen(false)} className="btn-secondary">Cancel</button>
          </div>
        </form>
      </Modal>

      {/* ── Culture section modal ── */}
      <Modal isOpen={cultureModalOpen} onClose={() => setCultureModalOpen(false)} title="Edit Culture Section" size="lg">
        <form onSubmit={cultureForm.handleSubmit(onCultureSubmit)} className="space-y-4">
          <div>
            <label className="label">Section image</label>
            <ImageUploader
              folder="careerCulture"
              maxFiles={1}
              existingImages={cultureImage ? [cultureImage] : []}
              onUpload={(urls) => setCultureImage(urls[0] || "")}
            />
          </div>
          <div>
            <label className="label">Badge label</label>
            <input {...cultureForm.register("badge")} className="input" placeholder="e.g. Our Culture" />
          </div>
          <div>
            <label className="label">Heading</label>
            <input {...cultureForm.register("heading")} className="input" placeholder="e.g. A Place Where Trust Meets Happiness" />
          </div>
          <div>
            <label className="label">Paragraph</label>
            <textarea {...cultureForm.register("paragraph")} rows={4} className="input resize-none" placeholder="Short description of your workplace culture" />
          </div>
          <div className="flex gap-3 pt-2">
            <button type="submit" disabled={cultureSaving} className="btn-primary flex-1 flex items-center justify-center gap-2">
              {cultureSaving ? <><Spinner size="sm" /> Saving...</> : "Save Section"}
            </button>
            <button type="button" onClick={() => setCultureModalOpen(false)} className="btn-secondary">Cancel</button>
          </div>
        </form>
      </Modal>
    </div>
  );
}